import { SelectedPassages } from '@/components/unit/SelectedPassages';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useHideLayoutFooter, useHideLayoutHeader } from '@/contexts/LayoutChromeContext';
import { useHowieDojoWebSocket } from '@/hooks/useHowieDojoWebSocket';
import type { DojoBeat, DojoExperienceLevel, DojoMessage, DojoUnitsCatalog } from '@/types/dojo';
import { cn } from '@/lib/utils';
import { HowieComposer } from '@/components/HowieComposer';
import { LevelPicker, shouldPickLevel } from '@/components/unit/LevelPicker';
import { UnitPicker } from '@/components/unit/UnitPicker';
import { UnitBeatStepper } from '@/components/unit/UnitBeatStepper';
import { VignettePanel } from '@/components/unit/VignettePanel';
import { UnitChat } from '@/components/unit/UnitChat';
import { InsightCapture, MAX_INSIGHT_CHARS } from '@/components/unit/InsightCapture';
import { RoundComplete } from '@/components/unit/RoundComplete';
import { HOWIE_PRACTICE_COMPLETED_EVENT } from '@/lib/howieEvents';
import { clearChatDraft, loadChatDraft, saveChatDraft } from '@lightning/utils';

const TERMINAL_BEATS: DojoBeat[] = ['completed', 'abandoned'];
const COMPOSER_HIDDEN_BEATS: DojoBeat[] = ['idle', 'chiddush', 'completed', 'abandoned'];
// Beats that introduce new reading: the story to mark and the flipped case.
const VIGNETTE_OPEN_BEATS: DojoBeat[] = ['highlight', 'enact'];
// A split workspace earns its complexity only when the learner needs to keep the
// source text in view while marking it or answering from it.
const SPLIT_WORKSPACE_BEATS: DojoBeat[] = ['highlight', 'react_well', 'react_better', 'enact'];

/**
 * DojoPage — the unit surface.
 *
 * Beat shape: Arrive → Mark → Notice → Sharpen → Your Move → Insight. This file
 * is only the shell: which panel is on screen for the current beat, how the two
 * columns are sized, the opening question, and the learner's marks in
 * flight. Everything with an opinion lives in components/unit/.
 */
export default function DojoPage() {
  const location = useLocation();
  // Local visual review only: never connects to the practice service or writes history.
  if (import.meta.env.DEV && new URLSearchParams(location.search).get('preview') === 'completion') {
    return <CompletionPreview />;
  }
  return <DojoExperience />;
}

function CompletionPreview() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState(false);
  return (
    <div className="h-full overflow-y-auto px-4 py-12 sm:px-8">
      <p className="mb-6 text-center text-sm text-how-text-secondary">Completion preview · nothing is saved</p>
      {saved ? <RoundComplete
        imperativeLabel="Navigating competing stakeholder priorities"
        imperativeId="competing_stakeholders"
        imperativeProgress={null}
        judgeSummary={null}
        savedReflection={draft.trim()}
        onBackToPractice={() => navigate('/dojo')}
      /> : <InsightCapture
        value={draft}
        onChange={setDraft}
        onSave={() => setSaved(true)}
        onSkip={() => navigate('/dojo')}
        onNextPractice={() => navigate('/dojo')}
      />}
    </div>
  );
}

function DojoExperience() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [insightDraft, setInsightDraft] = useState('');
  const submittedReflection = useRef<{ roundId: string | null; text: string } | null>(null);
  const [leaveAfterCompletion, setLeaveAfterCompletion] = useState(false);
  const nextPracticeAfterCompletion = useRef<string | null>(null);
  const [pendingStartUnitId, setPendingStartUnitId] = useState<string | null | undefined>();
  const [levelPickerOpen, setLevelPickerOpen] = useState(false);
  const [previewLevel, setPreviewLevel] = useState<DojoExperienceLevel | null>(null);
  const [vignetteCollapsed, setVignetteCollapsed] = useState(false);
  const [intakeMessages, setIntakeMessages] = useState<DojoMessage[]>([]);
  // Set on "Done marking". The marks are on the server from then on, so they lock
  // while Howie reads them back; an edit now would show on screen and be lost.
  const [marksSubmitted, setMarksSubmitted] = useState(false);
  const vignetteRef = useRef<HTMLDivElement>(null);
  const vignetteContentRef = useRef<HTMLDivElement>(null);
  const celebratedRoundRef = useRef<string | null>(null);

  const {
    messages,
    currentBeat,
    currentRoundId,
    currentUnit,
    // The learner's own marks live in the hook so a mid-round refresh, which
    // rehydrates them off the round, does not lose them.
    learnerMarks,
    setLearnerMarks,
    catalog,
    profile,
    lastCompletion,
    streamingText,
    isLoading,
    isLoadingCatalog,
    isSending,
    isStreaming,
    canSteer,
    connectionStatus,
    hasError,
    error,
    catalogError,
    draft: inputValue,
    setDraft: setInputValue,
    startRound,
    sendMessage,
    submitMarks,
    advanceBeat,
    setExperienceLevel,
    saveInsight,
    endRoundEarly,
    clearRound,
    refreshCatalog,
  } = useHowieDojoWebSocket({ userId: user?.id });

  const insightDraftSurface = `dojo-insight:${user?.id || 'anonymous'}`;
  const inRound = currentBeat !== 'idle';
  const isTerminalBeat = TERMINAL_BEATS.includes(currentBeat);
  const isFirstTimePreview = import.meta.env.DEV
    && new URLSearchParams(location.search).get('preview') === 'first-time';
  const showLevelPicker = isFirstTimePreview
    ? previewLevel === null
    : shouldPickLevel(profile) || levelPickerOpen;
  // Presentation-only copy: never reset the account or use its recommendation
  // as evidence of what a new learner would be offered.
  const freshPreviewCatalog = useMemo<DojoUnitsCatalog | null>(() => {
    if (!isFirstTimePreview || !catalog) return null;
    return {
      ...catalog,
      profile: null,
      progress: null,
      mastery: null,
      rounds_completed: 0,
      recommended_unit_id: null,
      recommended_reason: 'catalog_default',
      imperatives: catalog.imperatives.map((item) => ({ ...item, units_completed: 0 })),
      units: catalog.units.map((unit) => ({
        ...unit,
        status: 'available',
        latest_unit_rating: null,
        last_practiced_at: null,
        latest_round_id: null,
      })),
    };
  }, [catalog, isFirstTimePreview]);

  useHideLayoutFooter(inRound && !isTerminalBeat);
  useHideLayoutHeader(inRound && !isTerminalBeat);

  useEffect(() => {
    setInsightDraft(loadChatDraft('howie', insightDraftSurface, currentRoundId));
  }, [currentRoundId, insightDraftSurface]);

  // A new round clears the opening question.
  useEffect(() => {
    setIntakeMessages([]);
    setVignetteCollapsed(false);
  }, [currentRoundId]);

  // Marks unlock again on any new beat, or if the server refused them.
  useEffect(() => {
    setMarksSubmitted(false);
  }, [currentBeat, currentRoundId]);
  useEffect(() => {
    if (hasError) setMarksSubmitted(false);
  }, [hasError]);

  useEffect(() => {
    if (
      currentBeat !== 'completed' ||
      !lastCompletion?.roundId ||
      celebratedRoundRef.current === lastCompletion.roundId
    ) {
      return;
    }

    celebratedRoundRef.current = lastCompletion.roundId;
    window.dispatchEvent(new CustomEvent(HOWIE_PRACTICE_COMPLETED_EVENT));
  }, [currentBeat, lastCompletion?.roundId]);

  // Q1 is rendered client-side the moment the unit lands — no model round-trip, so
  // the round opens instantly.
  useEffect(() => {
    if (currentBeat !== 'intake' || !currentUnit) return;
    setIntakeMessages((prev) => {
      if (prev.length > 0) return prev;
      const question = currentUnit.intake_questions[0];
      if (!question) return prev;
      return [
        {
          id: `intake-q0-${currentUnit.scenario_id}`,
          role: 'assistant',
          content: question,
          timestamp: new Date().toISOString(),
          beat: 'intake',
        },
      ];
    });
  }, [currentBeat, currentUnit]);

  // Start each step at the story heading, without changing the learner's marks.
  useEffect(() => {
    if (vignetteRef.current) vignetteRef.current.scrollTop = 0;
    if (vignetteContentRef.current) vignetteContentRef.current.scrollTop = 0;
  }, [currentBeat, currentRoundId]);

  // Mobile: new reading opens the activity; finishing marking makes room for
  // discussion. The toggle can reopen the story and marks at any time.
  // Desktop keeps the activity visible through the lg:block override.
  useEffect(() => {
    if (currentBeat === 'react_well') {
      setVignetteCollapsed(true);
      return;
    }
    if (!VIGNETTE_OPEN_BEATS.includes(currentBeat)) return;
    setVignetteCollapsed(false);
    if (currentBeat === 'highlight') {
      vignetteRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentBeat, currentRoundId]);

  const transcript = useMemo(
    // Howie asks the Notice and Sharpen questions himself, so they arrive in
    // the transcript like everything else he says.
    () => [...intakeMessages, ...messages],
    [intakeMessages, messages]
  );

  const beginRound = useCallback(
    async (unitId?: string) => {
      setInsightDraft('');
      await startRound(undefined, unitId);
    },
    [startRound]
  );

  const handleStartUnit = async (unitId?: string) => {
    if (insightDraft.trim()) {
      setPendingStartUnitId(unitId ?? null);
      return;
    }
    await beginRound(unitId);
  };


  const handleSendMessage = async () => {
    const message = inputValue.trim();
    if (!message) return;
    // Intake answers go to Howie like any other message. They used to be
    // intercepted here: the client stored them, pushed the next question from a
    // static array, and sent nothing until it had both — so the coach could not
    // read the first answer before the second question appeared, and could
    // never push back on an answer that gave it nothing. The server now saves
    // each answer as it lands and holds the beat until both are in.
    setInputValue('');
    await sendMessage(message);
  };

  const handleSaveInsight = async () => {
    if (!insightDraft.trim()) return;
    submittedReflection.current = { roundId: currentRoundId, text: insightDraft.trim() };
    await saveInsight(insightDraft.trim(), false);
    // Sending is not an acknowledgement: retain the draft until the server saves it.
  };

  const handleSelectLevel = async (level: DojoExperienceLevel) => {
    if (isFirstTimePreview) {
      setPreviewLevel(level);
      return;
    }
    await setExperienceLevel(level);
    setLevelPickerOpen(false);
  };

  const handleBackToPractice = useCallback(() => {
    // Leaving is not discarding: round-scoped drafts restore on Continue.
    setInsightDraft('');
    clearRound();
    void refreshCatalog();
  }, [clearRound, refreshCatalog]);

  useEffect(() => {
    if (lastCompletion?.roundId === currentRoundId && lastCompletion?.insightId) {
      clearChatDraft('howie', insightDraftSurface, currentRoundId);
      setInsightDraft('');
    }
  }, [lastCompletion, currentRoundId, insightDraftSurface]);

  useEffect(() => {
    if (leaveAfterCompletion && currentBeat === 'completed' && lastCompletion?.roundId === currentRoundId) {
      setLeaveAfterCompletion(false);
      const nextUnitId = nextPracticeAfterCompletion.current;
      nextPracticeAfterCompletion.current = null;
      if (nextUnitId) void beginRound(nextUnitId);
      else handleBackToPractice();
    } else if (leaveAfterCompletion && hasError) {
      setLeaveAfterCompletion(false);
      nextPracticeAfterCompletion.current = null;
    }
  }, [leaveAfterCompletion, currentBeat, lastCompletion, currentRoundId, hasError, handleBackToPractice, beginRound]);

  const practiceRouteState = location.state as {
    showPracticeLibrary?: boolean;
    startUnitId?: string;
  } | null;
  const showPracticeLibrary = Boolean(practiceRouteState?.showPracticeLibrary);
  const startUnitId = practiceRouteState?.startUnitId;

  useEffect(() => {
    if (!showPracticeLibrary) return;
    handleBackToPractice();
    navigate('/dojo', { replace: true, state: null });
  }, [handleBackToPractice, navigate, showPracticeLibrary]);

  useEffect(() => {
    if (
      !startUnitId ||
      currentBeat !== 'idle' ||
      showLevelPicker ||
      isLoadingCatalog ||
      connectionStatus !== 'connected'
    ) {
      return;
    }

    const unitExists = catalog?.units.some((unit) => unit.unit_id === startUnitId);
    navigate('/dojo', { replace: true, state: null });
    if (unitExists) void beginRound(startUnitId);
  }, [
    beginRound,
    catalog?.units,
    connectionStatus,
    currentBeat,
    isLoadingCatalog,
    navigate,
    showLevelPicker,
    startUnitId,
  ]);

  const recommendedUnitId = catalog?.recommended_unit_id ?? undefined;
  const composerHidden = COMPOSER_HIDDEN_BEATS.includes(currentBeat);
  const usesSplitWorkspace = SPLIT_WORKSPACE_BEATS.includes(currentBeat);
  const passageIsPrimary = currentBeat === 'highlight';
  const startDisabled = isLoading || isSending || connectionStatus !== 'connected';

  const markingActions = currentBeat === 'highlight' ? (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <span className="text-xs text-how-text-on-paper/50" role="status">
        {learnerMarks.length > 0
          ? `${learnerMarks.length} passage${learnerMarks.length === 1 ? '' : 's'} marked`
          : 'Select a passage to begin.'}
      </span>
      <button type="button" onClick={() => { setMarksSubmitted(true); void submitMarks(learnerMarks); }}
        disabled={isSending || marksSubmitted}
        className="rounded-lg border border-how-accent-warm px-4 py-2 text-sm font-semibold text-how-accent-warm disabled:opacity-50">
        Done
      </button>
    </div>
  ) : null;

  const discussionNext = currentBeat === 'react_well'
    ? { beat: 'react_better' as DojoBeat, label: 'Explore what could improve' }
    : currentBeat === 'react_better'
      ? { beat: 'enact' as DojoBeat, label: 'Try the next case' }
      : currentBeat === 'enact'
        ? { beat: 'chiddush' as DojoBeat, label: 'Finish discussion' }
        : null;
  const discussionTurns = messages.filter(message => message.role === 'user' && message.beat === currentBeat).length;
  const canContinueDiscussion = discussionTurns >= (currentBeat === 'enact' ? 2 : 1);

  const chatFooter = (
    <>
      {currentBeat === 'chiddush' && (
        <InsightCapture
          key={currentRoundId}
          value={insightDraft}
          onChange={(value) => {
            setInsightDraft(value);
            saveChatDraft('howie', insightDraftSurface, currentRoundId, value.slice(0, MAX_INSIGHT_CHARS));
          }}
          onSave={handleSaveInsight}
          onSkip={() => {
            nextPracticeAfterCompletion.current = null;
            setLeaveAfterCompletion(true);
            void advanceBeat('completed');
          }}
          onNextPractice={recommendedUnitId && recommendedUnitId !== currentUnit?.scenario_id ? () => {
            // Finish the current round before beginning another; never abandon it.
            nextPracticeAfterCompletion.current = recommendedUnitId;
            setLeaveAfterCompletion(true);
            void advanceBeat('completed');
          } : undefined}
          disabled={isSending || leaveAfterCompletion}
        />
      )}

      {currentBeat === 'completed' && currentUnit && (
        <RoundComplete
          imperativeLabel={currentUnit.imperative_label}
          imperativeId={currentUnit.imperative_id}
          imperativeProgress={lastCompletion?.imperativeProgress ?? null}
          judgeSummary={lastCompletion?.judgeSummary ?? null}
          savedReflection={lastCompletion?.roundId === currentRoundId && lastCompletion?.insightId && submittedReflection.current?.roundId === currentRoundId
            ? submittedReflection.current.text
            : undefined}
          onNextUnit={
            recommendedUnitId && recommendedUnitId !== currentUnit.scenario_id
              ? () => void handleStartUnit(recommendedUnitId)
              : undefined
          }
          onBackToPractice={handleBackToPractice}
          onViewYourHow={() => navigate('/your-how')}
          disabled={startDisabled}
        />
      )}

      {currentBeat === 'abandoned' && (
        <div className="mx-auto max-w-2xl rounded-2xl border border-how-space-deep/[0.05] bg-how-paper/55 p-6 text-center">
          <h2 className="font-body text-[24px] font-medium leading-[30px] text-how-space-deep">Practice ended</h2>
          <p className="mx-auto mt-2 max-w-md text-[14px] leading-[22px] text-gray-500">
            Nothing was saved to YOUR HOW. Choose another unit when you are ready.
          </p>
          <button
            type="button"
            onClick={handleBackToPractice}
            className="mt-5 inline-flex items-center justify-center gap-2 rounded-lg bg-how-accent-warm px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-how-red-hover"
          >
            Back to Dojo
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </>
  );

  return (
    <div
      className="absolute inset-0 flex flex-col text-how-space-deep"
      style={{
        background: inRound
          ? 'var(--how-paper-soft)'
          : 'linear-gradient(180deg, var(--how-paper-soft) 0%, var(--color-how-page-wash) 100%)',
      }}
    >
      {inRound && !isTerminalBeat && currentUnit && currentBeat !== 'intake' && (
        <div className="flex-shrink-0 border-b border-gray-100 px-4 py-3 sm:px-8 sm:py-5">
          <UnitBeatStepper
            beat={currentBeat}
            contextLabel={currentUnit.imperative_label}
            onBackToPractice={handleBackToPractice}
            onEndRound={isTerminalBeat ? undefined : () => void endRoundEarly()}
            endRoundDisabled={isSending}
            onContinue={discussionNext && canContinueDiscussion ? () => void advanceBeat(discussionNext.beat) : undefined}
            continueLabel={discussionNext?.label}
            continueDisabled={isSending || isStreaming || connectionStatus !== 'connected'}
          />
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-hidden">
        {showLevelPicker ? (
          <div className="dojo-scroll h-full overflow-y-auto px-4 sm:px-8">
            <LevelPicker onSelect={handleSelectLevel} disabled={isSending} />
          </div>
        ) : !inRound || isFirstTimePreview ? (
          <div className="dojo-scroll h-full overflow-y-auto px-4 py-10 sm:px-8 sm:py-12 lg:py-14">
            <div className="mx-auto w-full max-w-4xl">
              <UnitPicker
                catalog={isFirstTimePreview ? freshPreviewCatalog : catalog}
                isLoading={isLoadingCatalog}
                error={catalogError}
                onRetry={() => void refreshCatalog()}
                onStartUnit={(unitId) => { if (!isFirstTimePreview) void handleStartUnit(unitId); }}
                onReviewRound={isFirstTimePreview ? undefined : (roundId) => navigate(`/dojo/round/${roundId}`)}
                isFirstSession={profile?.is_first_session ?? false}
                disabled={isFirstTimePreview || startDisabled}
              />
              {isFirstTimePreview && <p className="mt-8 text-center text-sm text-how-text-secondary">
                First-time preview · nothing is saved. Begin is disabled; the starting suggestion uses catalog order.
              </p>}
            </div>
          </div>
        ) : usesSplitWorkspace ? (
          <div className="grid h-full grid-cols-1 grid-rows-[auto_minmax(0,1fr)] lg:grid-cols-12 lg:grid-rows-1">
            {currentUnit && (
              <div
                ref={vignetteRef}
                className={cn(
                  'dojo-scroll flex min-h-0 max-h-[45dvh] flex-col border-b border-how-space-deep/[0.08] bg-how-paper-warm lg:max-h-none lg:overflow-y-auto lg:border-b-0',
                  passageIsPrimary ? 'lg:col-span-8' : 'lg:col-span-7'
                )}
              >
                <button
                  type="button"
                  onClick={() => setVignetteCollapsed((prev) => !prev)}
                  aria-expanded={!vignetteCollapsed}
                  className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-2 text-left text-xs text-gray-400 lg:hidden"
                >
                  <span className="truncate">{currentUnit.title}</span>
                  {vignetteCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                </button>
                <div
                  ref={vignetteContentRef}
                  className={cn(
                    'min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 lg:px-8',
                    vignetteCollapsed && 'hidden lg:block'
                  )}
                >
                  <VignettePanel
                    unit={currentUnit}
                    beat={currentBeat}
                    marks={learnerMarks}
                    onMarksChange={marksSubmitted ? undefined : setLearnerMarks}
                    footer={markingActions}
                    embedded
                  />
                </div>
              </div>
            )}

            <div
              className={cn(
                'flex min-h-0 flex-col bg-white lg:border-l lg:border-how-space-deep/[0.08]',
                currentUnit
                  ? passageIsPrimary
                    ? 'lg:col-span-4'
                    : 'lg:col-span-5'
                  : 'lg:col-span-12'
              )}
            >
              {/* The conversation and its composer are one column. The composer
                  used to span the whole page, which capped the vignette from
                  underneath and left the reader scrolling a short window of a
                  500-word story. Keeping it here lets the vignette own the full
                  height of its own column. */}
              <div className="dojo-scroll min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 lg:px-8">
                <div className="mx-auto w-full max-w-2xl">
                  <UnitChat
                    messages={transcript}
                    streamingText={streamingText}
                    isStreaming={isStreaming}
                    isSending={isSending}
                    error={hasError ? error : null}
                    footer={chatFooter}
                    collapseBeforeBeat={currentBeat}
                  />
                </div>
              </div>

              {inRound && !composerHidden && (
                <div className="flex-shrink-0 bg-white px-4 py-4 sm:px-6 lg:px-8">
                  <SelectedPassages marks={learnerMarks} beat={currentBeat} />
                  <HowieComposer
                    value={inputValue}
                    onChange={setInputValue}
                    onSend={handleSendMessage}
                    disabled={connectionStatus !== 'connected'}
                    sending={isSending}
                    allowSendWhileSending={canSteer}
                    placeholder={currentBeat === 'highlight' ? 'Ask Howie' : 'Respond to Howie...'}
                    className="mx-auto max-w-2xl"
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex h-full min-h-0 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-8 pt-4 sm:px-8 sm:pt-10">
              <div className="mx-auto w-full max-w-2xl">
                {currentBeat === 'intake' && currentUnit && (
                  <VignettePanel
                    unit={currentUnit}
                    beat={currentBeat}
                    marks={learnerMarks}
                    roundControls={(
                      <UnitBeatStepper
                        beat={currentBeat}
                        onBackToPractice={handleBackToPractice}
                        onEndRound={() => void endRoundEarly()}
                        endRoundDisabled={isSending}
                        onContinue={discussionNext && canContinueDiscussion ? () => void advanceBeat(discussionNext.beat) : undefined}
                        continueLabel={discussionNext?.label}
                        continueDisabled={isSending || isStreaming || connectionStatus !== 'connected'}
                      />
                    )}
                  />
                )}

                <div
                  className={cn(
                    currentBeat === 'intake' && 'mt-5 border-t border-how-space-deep/[0.06] pt-5 sm:mt-8 sm:pt-8'
                  )}
                >
                  <UnitChat
                    messages={transcript}
                    streamingText={streamingText}
                    isStreaming={isStreaming}
                    isSending={isSending}
                    error={hasError ? error : null}
                    footer={chatFooter}
                  />
                </div>
              </div>
            </div>

            {inRound && !composerHidden && (
              <div className="relative flex-shrink-0 bg-[linear-gradient(180deg,transparent_0%,var(--how-paper-soft)_24%)] px-4 pb-7 pt-6 sm:px-8 sm:pb-8">
                <div className="mx-auto w-full max-w-2xl">
                  <HowieComposer
                    value={inputValue}
                    onChange={setInputValue}
                    onSend={handleSendMessage}
                    disabled={connectionStatus !== 'connected'}
                    sending={isSending}
                    allowSendWhileSending={canSteer}
                    placeholder="Your response..."
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {pendingStartUnitId !== undefined && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="discard-insight-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-how-space-deep/35 px-4"
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-popover">
            <h2
              id="discard-insight-title"
              className="font-body text-lg font-medium text-how-space-deep"
            >
              Discard this insight?
            </h2>
            <p className="mt-2 text-sm leading-6 text-how-text-secondary">
              Your current words have not been saved to YOUR HOW.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingStartUnitId(undefined)}
                className="rounded-lg border border-how-accent-warm/20 px-4 py-2 text-sm font-semibold text-how-space-deep transition-colors hover:bg-how-paper/60"
              >
                Keep writing
              </button>
              <button
                type="button"
                onClick={() => {
                  const unitId = pendingStartUnitId ?? undefined;
                  setPendingStartUnitId(undefined);
                  void beginRound(unitId);
                }}
                disabled={isLoading || isSending}
                className="rounded-lg bg-how-accent-warm px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-how-red-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
